import React, { useState } from 'react';

/**
 * Série mensal em colunas.
 *
 * Vertical, ao contrário das outras barras do sistema: aqui o eixo de categorias
 * é TEMPO, e tempo se lê da esquerda para a direita. Deitada, a série viraria uma
 * lista de meses e a subida deixaria de ser visível — que é a única coisa que se
 * olha num gráfico de mês a mês.
 *
 * A MÉDIA DO PERÍODO entra como linha tracejada. Sem ela, "R$ 12.400 em agosto" é
 * um número solto: alto ou baixo só existe contra alguma coisa, e o mês anterior
 * sozinho é uma base frágil demais para dizer que o mês foi bom.
 *
 * Mês zerado mantém a coluna no chão, e não some: o buraco no meio da série é
 * exatamente o que se quer ver.
 */

const COR = '#2a78d6';
const TRILHO = '#e6f0fd';

const emReais = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * A mesma coluna serve dinheiro e contagem.
 *
 * `unidade` troca o formato do número no balão: sem ela, "12 serviços" sairia
 * escrito como "R$ 12" — e um gráfico que chama serviço de real é pior que
 * gráfico nenhum.
 */
const ColunasMensais = ({ dados = [], altura = 'h-40', unidade = null }) => {
  const escreve = (v) => (unidade
    ? `${Number(v || 0).toLocaleString('pt-BR')} ${unidade}`
    : emReais(v));

  const [ativo, setAtivo] = useState(null);

  if (!dados || dados.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">Sem movimento no período.</p>;
  }

  const valores = dados.map(d => Number(d.valor) || 0);
  const maximo = Math.max(...valores, 1);
  const media = valores.reduce((t, v) => t + v, 0) / valores.length;

  return (
    <div>
      <div className={`relative flex items-end gap-1 ${altura}`}>
        {/* A linha da média, atrás das colunas. */}
        {media > 0 && (
          <div
            className="absolute left-0 right-0 border-t border-dashed border-slate-300 pointer-events-none"
            style={{ bottom: `${(media / maximo) * 100}%` }}
            title={`Média do período: ${escreve(media)}`}
          />
        )}

        {dados.map(d => {
          const valor = Number(d.valor) || 0;
          const proporcao = valor > 0 ? Math.max((valor / maximo) * 100, 1.5) : 0;
          const destacado = ativo === d.chave;

          return (
            <div
              key={d.chave}
              className="flex-1 min-w-0 h-full flex flex-col justify-end relative"
              onMouseEnter={() => setAtivo(d.chave)}
              onMouseLeave={() => setAtivo(null)}
            >
              {destacado && (
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                  <div className="bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap">
                    {d.rotulo}: {escreve(valor)}
                  </div>
                </div>
              )}

              <div
                className="w-full rounded-t transition-[height] duration-300 ease-out"
                style={{
                  height: `${proporcao}%`,
                  backgroundColor: valor > 0 ? COR : TRILHO,
                  opacity: destacado ? 1 : 0.92,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* O rótulo fica FORA da área das colunas, senão a coluna baixa o empurra
          para dentro do gráfico. Gira no estreito em vez de sumir. */}
      <div className="flex gap-1 mt-1.5">
        {dados.map(d => (
          <div key={d.chave}
            className="flex-1 min-w-0 text-center text-[10px] text-slate-500 truncate"
            title={d.rotulo}>
            {d.rotulo}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ColunasMensais;
